import { Component, Input, OnDestroy, OnInit } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";
import { map } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { AppState, contractsStateTableConfig } from "src/app/store/reducers";
import {
  getContractExcelList,
  getContractList,
  resetContractsState,
} from "src/app/store/actions/contracts/contracts.actions";

import {
  ColumnSorterInfo,
  DefaultPage,
  ExportEvent,
  FilterEvent,
  FilterName,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import {
  removeColumnSorterInfoOptions,
  removeTableConfigOptions,
  setAdvancedFilter,
} from "src/app/shared/utils/tableUtils";

import { BaseContractsList } from "src/app/shared/base-classes/base-contracts-list/base-contracts-list";

const USAGE_CODE = "CUSTOMER.CONTRACT";

@Component({
  selector: "customer-contracts-list",
  templateUrl: "./customer-contracts-list.component.html",
  styleUrls: ["./customer-contracts-list.component.scss"],
})
export class CustomerContractsListComponent
  extends BaseContractsList
  implements OnInit, OnDestroy
{
  @Input() set code(code: string) {
    if (code) {
      this._code = code;
      this.getTableConfig(USAGE_CODE);
      this.getContracts();
      this.setTableCustomElements();
    }
  }

  _code: string;
  constructor(store: Store<AppState>, private translate: TranslateService) {
    super(store);

    this.columnInfo$ = this.store.pipe(select(contractsStateTableConfig)).pipe(
      map((columnInfo) => {
        if (columnInfo) {
          return removeColumnSorterInfoOptions(columnInfo);
        }
      })
    );
  }

  ngOnInit(): void {
    this.setTranslatedTitle();
    this.tableConfig = removeTableConfigOptions(this.tableConfig);
  }

  setTranslatedTitle() {
    this.translate
      .get("Tabs.Contracts")
      .subscribe((translatedTitle) => (this.tableTitle = translatedTitle));
  }

  getContracts(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    const advancedFilter = this.setAdvancedFilter(filterEvent);

    this.store.dispatch(
      getContractList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: advancedFilter,
        },
      })
    );
  }

  getContractsExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    const advancedFilter = this.setAdvancedFilter(filterEvent);

    this.store.dispatch(
      getContractExcelList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: advancedFilter,
        },
      })
    );
  }

  setAdvancedFilter(filterEvent: FilterEvent) {
    const customerContractsFilter = {
      column: "customer.code",
      operator: FilterName.EQUALS_TO,
      value: this._code,
    };

    return setAdvancedFilter(customerContractsFilter, filterEvent);
  }

  onGetTableData(tableEvent: TableEvent) {
    this.getTableData(tableEvent, this.getContracts.bind(this));
  }

  onGetExportData(exportEvent: ExportEvent) {
    this.getExportData(exportEvent, this.getContractsExcelList.bind(this));
  }

  onSaveTableConfig(columnInfo: ColumnSorterInfo[]) {
    this.saveTableConfig(columnInfo, USAGE_CODE);
  }

  ngOnDestroy() {
    if (this.loadedContractSub) {
      this.loadedContractSub.unsubscribe();
    }
    this.store.dispatch(resetContractsState());
  }
}
